/**
 * Staff-only monthly Ambassador leaderboard (hidden from ambassadors for now).
 * GET /api/staff/affiliate-leaderboard?month=YYYY-MM
 */
import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Trophy, EyeOff } from 'lucide-react';
import API_URL from '@/config/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const monthLabel = (key) => {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
};
const shiftMonth = (key, delta) => {
  const [y, m] = key.split('-').map(Number);
  return monthKey(new Date(y, m - 1 + delta, 1));
};
const gbp = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const MEDAL = { 1: 'bg-amber-100 text-amber-800 border-amber-300', 2: 'bg-slate-100 text-slate-700 border-slate-300', 3: 'bg-orange-100 text-orange-800 border-orange-300' };

export const AmbassadorLeaderboard = () => {
  const [month, setMonth] = useState(monthKey(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const isCurrent = month === monthKey(new Date());

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetch(`${API_URL}/api/staff/affiliate-leaderboard?month=${month}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive) { setData(d); setLoading(false); } });
    return () => { alive = false; };
  }, [month]);

  const allRows = data?.rows || [];
  const rows = showAll ? allRows : allRows.slice(0, 10);

  return (
    <Card className="p-4 mb-4" data-testid="ambassador-leaderboard">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h2 className="font-bold text-gray-900 flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" /> Ambassador leaderboard</h2>
          <p className="text-[11px] text-gray-500 flex items-center gap-1 mt-0.5"><EyeOff className="w-3 h-3" /> Ox team only — not visible to ambassadors yet</p>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={() => setMonth((m) => shiftMonth(m, -1))} data-testid="leaderboard-prev" aria-label="Previous month"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm font-semibold text-gray-800 min-w-[140px] text-center" data-testid="leaderboard-month">{monthLabel(month)}</span>
          <Button variant="outline" size="sm" onClick={() => setMonth((m) => shiftMonth(m, 1))} disabled={isCurrent} data-testid="leaderboard-next" aria-label="Next month"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>
      {loading ? (
        <p className="text-sm text-gray-500 py-4 text-center">Loading…</p>
      ) : allRows.length === 0 ? (
        <p className="text-sm text-gray-500 py-4 text-center">No ambassadors yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-gray-500">
              <tr>
                <th className="text-left py-1.5 pr-2 w-10">#</th>
                <th className="text-left py-1.5 pr-2">Ambassador</th>
                <th className="text-right py-1.5 px-2">Signups</th>
                <th className="text-right py-1.5 px-2">Orders</th>
                <th className="text-right py-1.5 px-2">Gross sales</th>
                <th className="text-right py-1.5 px-2">Commission</th>
                <th className="text-right py-1.5 pl-2 text-gray-400">All-time signups</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t" data-testid={`leaderboard-row-${r.id}`}>
                  <td className="py-1.5 pr-2">
                    <span className={`inline-flex w-7 h-7 items-center justify-center rounded-full border text-xs font-bold ${MEDAL[r.rank] || 'border-gray-200 text-gray-600'}`}>{r.rank}</span>
                  </td>
                  <td className="py-1.5 pr-2">
                    <span className="font-semibold text-gray-900">{r.name}</span>
                    <span className="text-xs text-gray-400"> · /join/{r.slug}</span>
                    {r.status !== 'active' && <span className="ml-1 text-[10px] uppercase text-rose-600 font-bold">{r.status}</span>}
                  </td>
                  <td className="py-1.5 px-2 text-right font-bold text-gray-900">{r.signups}</td>
                  <td className="py-1.5 px-2 text-right text-gray-700">{r.orders}</td>
                  <td className="py-1.5 px-2 text-right text-gray-700">{gbp(r.gross_sales)}</td>
                  <td className="py-1.5 px-2 text-right text-gray-700">{gbp(r.commission)}</td>
                  <td className="py-1.5 pl-2 text-right text-gray-400">{r.signups_all_time}</td>
                </tr>
              ))}
            </tbody>
            {data?.totals && (
              <tfoot className="border-t-2 text-xs font-semibold text-gray-700">
                <tr>
                  <td colSpan={2} className="py-1.5 pr-2">Month total</td>
                  <td className="py-1.5 px-2 text-right" data-testid="leaderboard-total-signups">{data.totals.signups}</td>
                  <td className="py-1.5 px-2 text-right">{data.totals.orders}</td>
                  <td className="py-1.5 px-2 text-right">{gbp(data.totals.gross_sales)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            )}
          </table>
          {allRows.length > 10 && (
            <button type="button" onClick={() => setShowAll((v) => !v)} className="mt-2 text-xs font-semibold text-[#7A0B7A] hover:underline" data-testid="leaderboard-toggle-all">
              {showAll ? 'Show top 10 only' : `Show all ${allRows.length} ambassadors`}
            </button>
          )}
        </div>
      )}
    </Card>
  );
};
